<template>
  <div class="max-w-7xl mx-auto pb-12">
    <Head title="Reportes & Balances de Gestión" />

    <!-- 1. Header con Título, Estado y Botón de Exportación en PDF / Excel -->
    <ReportHeader
      :filters="filters"
      :account-id="account?.id"
    />

    <!-- 2. Filtro Interactivo de Rango de Fechas (Pills rápidos + Inputs) -->
    <ReportDateFilter
      :filters="filters"
      :current-tab="activeTab"
    />

    <!-- 3. Tarjetas Ejecutivas de KPIs (En la misma pantalla, visibles siempre) -->
    <ReportKpiCards :stats="report.stats" />

    <!-- 4. Barra de Pestañas (Tabs) para Navegar entre Reportes sin salir de la pantalla -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-1.5 mb-6 overflow-x-auto">
      <div class="flex items-center gap-1.5 min-w-max">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          class="px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 cursor-pointer"
          :class="[
            activeTab === tab.id
              ? 'bg-orange-500 text-white shadow-sm ring-2 ring-orange-200'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
          ]"
          @click="changeTab(tab.id)"
        >
          <i :class="tab.icon" />
          <span>{{ tab.label }}</span>
          <span
            v-if="tab.badge !== undefined"
            class="px-1.5 py-0.2 rounded-full text-[10px] font-black"
            :class="activeTab === tab.id ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-700'"
          >
            {{ tab.badge }}
          </span>
        </button>
      </div>
    </div>

    <!-- 5. Contenido de la Pestaña Activa -->
    <transition name="fade" mode="out-in">
      <!-- Tab 1: Balance Financiero, P&L y Rentabilidad -->
      <BalanceTab
        v-if="activeTab === 'balance'"
        :stats="report.stats"
        :profit="report.profit"
        :filters="filters"
      />

      <!-- Tab 2: Informe Detallado de Ventas -->
      <SalesTab
        v-else-if="activeTab === 'sales'"
        :sales="report.sales"
        :filters="filters"
      />

      <!-- Tab 3: Informe Detallado de Compras -->
      <PurchasesTab
        v-else-if="activeTab === 'purchases'"
        :purchases="report.purchases"
        :filters="filters"
      />

      <!-- Tab 4: Relación de Inventarios (Más recargados vs Más rápido se agotan) -->
      <InventoryRotationTab
        v-else-if="activeTab === 'inventory'"
        :inventory="report.inventory"
        :filters="filters"
      />

      <!-- Tab 5: Cuentas por Cobrar & Deudores -->
      <ReceivablesTab
        v-else-if="activeTab === 'receivables'"
        :receivables="report.receivables"
        :filters="filters"
      />
    </transition>
  </div>
</template>

<script>
import { Head } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import ReportHeader from './Partials/ReportHeader.vue'
import ReportDateFilter from './Partials/ReportDateFilter.vue'
import ReportKpiCards from './Partials/ReportKpiCards.vue'
import BalanceTab from './Partials/Tabs/BalanceTab.vue'
import SalesTab from './Partials/Tabs/SalesTab.vue'
import PurchasesTab from './Partials/Tabs/PurchasesTab.vue'
import InventoryRotationTab from './Partials/Tabs/InventoryRotationTab.vue'
import ReceivablesTab from './Partials/Tabs/ReceivablesTab.vue'

export default {
  name: 'ReportsIndex',

  components: {
    Head,
    ReportHeader,
    ReportDateFilter,
    ReportKpiCards,
    BalanceTab,
    SalesTab,
    PurchasesTab,
    InventoryRotationTab,
    ReceivablesTab,
  },

  layout: Layout,

  props: {
    report: {
      type: Object,
      required: true,
    },
    currentTab: {
      type: String,
      default: 'balance',
    },
    filters: {
      type: Object,
      required: true,
    },
    deposits: {
      type: Array,
      default: () => [],
    },
    account: {
      type: Object,
      default: () => ({}),
    },
    users: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      activeTab: this.currentTab || 'balance',
    }
  },

  computed: {
    tabs() {
      return [
        {
          id: 'balance',
          label: 'Balance & Rentabilidad (P&L)',
          icon: 'fa fa-chart-pie',
        },
        {
          id: 'sales',
          label: 'Informe de Ventas',
          icon: 'fa fa-shopping-cart',
          badge: this.report?.stats?.total_orders ?? 0,
        },
        {
          id: 'purchases',
          label: 'Informe de Compras',
          icon: 'fa fa-truck',
          badge: this.report?.stats?.total_shoppings_count ?? 0,
        },
        {
          id: 'inventory',
          label: 'Relación de Inventarios (Rotación & Reposición)',
          icon: 'fa fa-boxes',
          badge: this.report?.inventory?.fastest_depleting?.length ?? 0,
        },
        {
          id: 'receivables',
          label: 'Cuentas por Cobrar',
          icon: 'fa fa-hand-holding-usd',
          badge: this.report?.receivables?.debtors_count ?? 0,
        },
      ]
    },
  },

  watch: {
    currentTab(newTab) {
      if (newTab) {
        this.activeTab = newTab
      }
    },
  },

  methods: {
    changeTab(tabId) {
      this.activeTab = tabId
      this.$inertia.get(
        '/reports',
        {
          date_from: this.filters.date_from,
          date_to: this.filters.date_to,
          tab: tabId,
        },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
        },
      )
    },
  },
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
